import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowRight, Play } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { loadPlaybookProgress, calculateOverallProgress, getCompletedTaskCount } from '../services/progressService';
import { getDemoEnrollmentData } from '../services/enrollmentService';

// Use the new Playbook interface from our hook
interface Playbook {
  id: string;
  title: string;
  short_description: string;
  introduction?: string;
  badge?: string;
  card_image_url?: string;
  detail_image_url?: string;
  outcomes: string[];
  new_skills: string[];
  is_published: boolean;
  display_order: number;
  user_progress?: {
    completed_task_ids: string[];
    current_phase_id?: string;
    current_task_id?: string;
  };
}

interface PlaybookCardProps {
  playbook: Playbook;
  onClick: () => void;
  enrolledUsersCount?: number;
  index?: number;
}

const PlaybookCard: React.FC<PlaybookCardProps> = ({ playbook, onClick, enrolledUsersCount, index = 0 }) => {
  const [progressData, setProgressData] = useState<{
    hasStarted: boolean;
    overallProgress: number;
  }>({
    hasStarted: false,
    overallProgress: 0
  });
  
  // Get consistent enrollment data for this playbook
  const demoEnrolledUsers = getDemoEnrollmentData(
    playbook.id, 
    playbook.display_order || 0,
    enrolledUsersCount
  );
  
  // Use the user_progress data from the playbook if available
  useEffect(() => {
    if (playbook.user_progress) {
      const completedTasksCount = playbook.user_progress.completed_task_ids?.length || 0;
      // For now we'll use a simple calculation, but this should be improved
      // when we have total tasks count available
      const overallProgress = completedTasksCount > 0 ? 
        Math.min(Math.floor((completedTasksCount / 10) * 100), 100) : 0;
      
      setProgressData({
        hasStarted: completedTasksCount > 0,
        overallProgress: overallProgress
      });
    } else {
      // Fall back to the localStorage approach for now
      const savedProgress = loadPlaybookProgress(playbook.id);
      if (savedProgress) {
        const completedTasks = savedProgress.completedTasks || {};
        const completedTasksCount = getCompletedTaskCount(completedTasks);
        
        setProgressData({
          hasStarted: completedTasksCount > 0,
          overallProgress: Math.min(Math.floor((completedTasksCount / 10) * 100), 100)
        });
      }
    }
  }, [playbook]);
  
  // Determine button text based on progress
  const getActionText = () => {
    if (!progressData.hasStarted) {
      return 'View Details';
    } else if (progressData.overallProgress === 100) {
      return 'View Completed';
    } else {
      return 'Continue';
    }
  };
  
  return (
    <Card className="overflow-hidden flex flex-col h-full w-full max-w-[360px] transition-all duration-200 hover:shadow-lg hover:-translate-y-1 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-card dark:bg-zinc-800">
      {/* Card Image with Badge */}
      <div className="relative h-[162px]">
        {/* Make the image clickable by wrapping it in a button */}
        <button
          type="button"
          onClick={onClick}
          className="w-full h-full block focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-t-[14px] overflow-hidden group"
          tabIndex={0}
          aria-label={`Open details for ${playbook.title}`}
          style={{ padding: 0, border: 'none', background: 'none' }}
        >
          <img 
            src={playbook.card_image_url} 
            alt={playbook.title}
            className="w-full h-full object-cover group-hover:brightness-95 transition duration-150"
            onError={(e) => {
              // Fallback image if the specified image fails to load
              e.currentTarget.src = `https://source.unsplash.com/random/600x400/?${playbook.title.toLowerCase().replace(/\s+/g, '-')}`;
            }}
          />
        </button>
        
        {/* Progress indicator - positioned over the image */}
        {/* Removed circular progress indicator for cleaner design */}
        
        {/* Badge */}
        {/* Removed badge for cleaner design */}
      </div>
      
      <CardContent className="flex flex-col flex-grow p-5">
        {/* Title and Description */}
        <h3 className="text-xl font-semibold mb-3 line-clamp-2 text-zinc-900 dark:text-zinc-100 leading-tight">{playbook.title}</h3>
        <p className="text-zinc-600 dark:text-zinc-300 text-sm mb-4">{playbook.short_description}</p>
        
        {/* Outcomes */}
        <div className="mb-4">
          <h4 className="text-sm font-medium mb-2 text-zinc-700 dark:text-zinc-300">Outcomes</h4>
          <ul className="text-sm">
            {playbook.outcomes.slice(0, 2).map((outcome, index) => (
              <li key={index} className="flex items-start mb-1 line-clamp-1">
                <span className="text-blue-600 dark:text-blue-400 mr-2 flex-shrink-0">•</span>
                <span className="text-zinc-600 dark:text-zinc-300 line-clamp-1">{outcome}</span>
              </li>
            ))}
          </ul>
        </div>
        
        {/* New Skills */}
        <div className="mb-4">
          <h4 className="text-sm font-medium mb-2 text-zinc-700 dark:text-zinc-300">New Skills</h4>
          <div className="flex flex-wrap gap-2">
            {playbook.new_skills.map((skill, index) => (
              <Badge key={index} variant="outline" className="bg-zinc-50 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-300 border-zinc-200 dark:border-zinc-600 font-normal whitespace-nowrap">
                {skill}
              </Badge>
            ))}
          </div>
        </div>
        
        {/* User Avatars (Social Proof) - using demo data for now */}
        <div className="mt-auto pt-4 flex items-center">
          <div className="flex -space-x-2 mr-3">
            {demoEnrolledUsers.avatars.slice(0, 5).map((avatar, index) => (
              avatar.type === 'image' ? (
                <img 
                  key={index}
                  src={avatar.url}
                  alt={`User ${index + 1}`}
                  className="w-6 h-6 rounded-full border-2 border-white dark:border-zinc-800 object-cover shadow-sm"
                  onError={(e) => {
                    // Fallback for avatar images
                    e.currentTarget.src = `https://ui-avatars.com/api/?name=User+${index + 1}&background=random`;
                  }}
                />
              ) : (
                <div 
                  key={index}
                  className="w-6 h-6 rounded-full border-2 border-white dark:border-zinc-800 shadow-sm flex items-center justify-center text-[10px] font-medium text-white bg-blue-500 dark:bg-blue-600 overflow-hidden"
                  style={{ backgroundImage: `url(${avatar.url})` }}
                >
                  {avatar.initials}
                </div>
              )
            ))}
          </div>
          <span className="text-xs text-zinc-500 dark:text-zinc-400">
            {demoEnrolledUsers.count} users enrolled
          </span>
        </div>
      </CardContent>
      
      <CardFooter className="p-5 pt-0">
        <Button 
          onClick={onClick}
          className="w-full gap-1.5 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 py-2.5 rounded-md focus:ring-2 focus:ring-blue-300 dark:focus:ring-blue-500 text-white"
        >
          {progressData.hasStarted && <Play className="h-4 w-4" />}
          {getActionText()}
          {!progressData.hasStarted && <ArrowRight className="h-4 w-4" />}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default PlaybookCard; 